import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { compose } from 'redux';
import { connect } from 'react-redux';
import { injectIntl, intlShape } from '../../util/reactIntl';
import { Page, LayoutSingleColumn, VimeoEmbed } from '../../components';
import TopbarContainer from '../../containers/TopbarContainer/TopbarContainer';
import FooterContainer from '../../containers/FooterContainer/FooterContainer';
import { withViewport } from '../../util/uiHelpers';
import { useConfiguration } from '../../context/configurationContext';

import css from './LandingPage.module.css';

//Static assets
import facebookImage from '../../assets/biketribe-facebook-sharing-1200x630.jpg';
import twitterImage from '../../assets/biketribe-twitter-sharing-600x314.jpg';
import { isScrollingDisabled } from '../../ducks/ui.duck';
import { getListingsById } from '../../ducks/marketplaceData.duck';

//Landing Page Sections

import SectionHero from './SectionHero/SectionHero';
import ExploreSection from './Sections/ExploreSection';
import SectionPopularCollections from './Sections/SectionPopularCollections';
import SectionExperience from './Sections/SectionExperience';
import SectionBestSeller from './Sections/SectionBestSeller';
import HomepageCoachesSection from './Sections/HomepageCoachesSection';
import SectionReadyToSell from './Sections/SectionReadyToSell';
import SectionCommunityPreview from './Sections/SectionCommunityPreview';
import SectionInspirationCTA from './Sections/SectionInspirationCTA';
import SectionContact from './Sections/SectionContact';
import { getEvents, getInstructorStates, getInstructors } from '../../util/strapiApi';

export const LandingPageComponent = props => {
  const { intl, scrollingDisabled, viewport, listings } = props;
  const [instructorStates, setInstructorStates] = useState([]);
  const [homepageInstructors, setHomepageInstructors] = useState([]);
  const [homepageEvents, setHomepageEvents] = useState([]);
  const [instructorsLoading, setInstructorsLoading] = useState(true);
  const [eventsLoading, setEventsLoading] = useState(true);
  const [instructorsError, setInstructorsError] = useState(false);
  const [eventsError, setEventsError] = useState(false);

  const config = useConfiguration();

  const marketplaceName = config.marketplaceName;

  const schemaTitle = intl.formatMessage({ id: 'LandingPage.schemaTitle' }, { marketplaceName });
  const schemaDescription = intl.formatMessage({
    id: 'LandingPage.schemaDescription',
  });
  const schemaImage = `${config.marketplaceRootURL}${facebookImage}`;

  useEffect(() => {
    let isActive = true;

    const loadHomepageContent = async () => {
      setInstructorsLoading(true);
      setEventsLoading(true);
      setInstructorsError(false);
      setEventsError(false);

      const [statesResult, instructorsResult, eventsResult] = await Promise.allSettled([
        getInstructorStates(),
        getInstructors(),
        getEvents({ upcoming: true }),
      ]);

      if (!isActive) return;

      if (statesResult.status === 'fulfilled') {
        setInstructorStates(statesResult.value || []);
      } else {
        setInstructorStates([]);
      }

      if (instructorsResult.status === 'fulfilled') {
        setHomepageInstructors(instructorsResult.value || []);
      } else {
        setHomepageInstructors([]);
      }

      if (eventsResult.status === 'fulfilled') {
        setHomepageEvents(eventsResult.value || []);
      } else {
        setHomepageEvents([]);
        setEventsError(true);
      }

      setInstructorsError(instructorsResult.status === 'rejected');
      setInstructorsLoading(false);
      setEventsLoading(false);
    };

    loadHomepageContent();

    return () => {
      isActive = false;
    };
  }, []);

  return (
    <Page
      className={css.root}
      scrollingDisabled={scrollingDisabled}
      contentType="website"
      description={schemaDescription}
      title={schemaTitle}
      facebookImages={[{ url: facebookImage, width: 1200, height: 630 }]}
      twitterImages={[
        {
          url: `${config.marketplaceRootURL}${twitterImage}`,
          width: 600,
          height: 314,
        },
      ]}
      schema={{
        '@context': 'http://schema.org',
        '@type': 'WebPage',
        description: schemaDescription,
        name: schemaTitle,
        image: [schemaImage],
      }}
    >
      <LayoutSingleColumn
        className={css.pageRoot}
        topbar={<TopbarContainer viewport={viewport} />}
        footer={<FooterContainer />}
      >
        <div className={css.pageContent}>
          <SectionHero intl={intl} viewport={viewport} />
          <SectionExperience />
          <ExploreSection />
          <SectionBestSeller listings={listings} viewport={viewport} />
          <SectionReadyToSell />
          <HomepageCoachesSection
            states={instructorStates}
            instructors={homepageInstructors}
            isLoading={instructorsLoading}
            hasError={instructorsError}
          />
          <SectionPopularCollections
            events={homepageEvents}
            isLoading={eventsLoading}
            hasError={eventsError}
          />
          {/* <SectionCommunityPreview /> */}
          <SectionInspirationCTA />
          {/* <section className={css.homepageVideoSection}>
            <div className={css.fixedWidthContainer}>
              <div className={css.homepageVideoWrapper}>
                <VimeoEmbed
                  className={css.homepageVideoEmbed}
                  src="https://player.vimeo.com/video/1173778853?h=4b65b967f5"
                />
              </div>
            </div>
          </section> */}
        </div>
      </LayoutSingleColumn>
    </Page>
  );
};

const { bool } = PropTypes;

LandingPageComponent.propTypes = {
  scrollingDisabled: bool.isRequired,
  intl: intlShape.isRequired,
};

const mapStateToProps = state => {
  const { currentPageResultIds } = state.LandingPage;
  const listings = getListingsById(state, currentPageResultIds);
  return {
    scrollingDisabled: isScrollingDisabled(state),
    listings,
  };
};

const LandingPage = compose(
  connect(
    mapStateToProps,
    null
  ),
  withViewport,
  injectIntl
)(LandingPageComponent);

export default LandingPage;
